import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal, Card, Button, Tag, Spin, Empty, message, Space, Typography, Select,
  Tooltip, Timeline, Drawer, Descriptions, Table, Alert, Collapse, Input,
  DatePicker, Form,
} from 'antd';
import {
  ReloadOutlined, CheckCircleFilled, CloseCircleFilled, ClockCircleFilled,
  SyncOutlined, FilterOutlined, ApiOutlined, ExportOutlined, DatabaseOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  listDagitRuns, getDagitRun, getDagitRunLogs, listConnections,
  launchDagitBackfill, listConnectorSpecs, getSchedulerStatus,
} from '../services/dataPlatformService';
import { openDagster, openDagsterRun, isDagitEnabled } from '../services/dagsterLink';
import { sourceLagSummary } from '../utils/sourceFreshness';

const ISO_DATE_FMT = 'YYYY-MM-DD';

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

// Maximum sync runs we ask the orchestrator for. The /syncs/dagit/runs
// endpoint caps at 500. We pull 200 — enough to cover several weeks of
// activity for a typical tenant — and surface a truncation banner when
// the response actually hits the ceiling so users know to filter down
// rather than scrolling forever and assuming they see everything.
const RUNS_FETCH_LIMIT = 200;

// Default look-back window of the time filter, in days.
const DEFAULT_RANGE_DAYS = 7;
// Poll interval while any run is queued/started/running.
const ACTIVE_REFRESH_MS = 5000;
const MS_PER_MINUTE = 60_000;

const ACTIVE_RUN_STATUSES = ['queued', 'started', 'starting', 'not_started'];

// ── Pure helpers (extracted from SyncHistoryDialog's hooks/render) ─────
export function computeHasActiveRuns(runs) {
  return runs.some((r) => ACTIVE_RUN_STATUSES.includes(r.status));
}

export function buildConnById(connections) {
  const m = {};
  for (const c of connections) {m[c.id] = c;}
  return m;
}

// Keeps runs whose start_time (falling back to creation_time) lies within
// `filterRange`. Either bound can be empty — an empty `from` means "since
// the beginning of time", an empty `to` means "until now".
export function filterRunsByTimeWindow(runs, filterRange) {
  const [from, to] = filterRange || [];
  if (!from && !to) {return runs;}
  const fromMs = from ? from.valueOf() : -Infinity;
  const toMs   = to   ? to.valueOf()   : Infinity;
  return runs.filter((r) => {
    const iso = r.start_time || r.creation_time;
    if (!iso) {return true;} // can't place it — don't hide it
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) {return true;}
    return t >= fromMs && t <= toMs;
  });
}

export function buildBackfillStreamOptions(conn) {
  if (!conn) {return [];}
  return (conn.selected_streams || []).map((name) => ({ value: name, label: name }));
}

// Days in the range the source cannot have published yet.
export function countUnpublishedDays(from, days, lag) {
  if (!lag) {return 0;}
  return Array.from({ length: days }, (_, i) => from.startOf('day').add(i, 'day'))
    .filter((d) => d.format(ISO_DATE_FMT) > lag.freshestDay).length;
}

export function computeBackfillPlan(conn, from, to, backfillStreams, specs) {
  if (!conn || !from || !to) {return null;}
  const days = to.startOf('day').diff(from.startOf('day'), 'day') + 1;
  if (days < 1) {return null;}
  const spec = specs.find((s) => s.name === conn.connector_name);
  const lag = sourceLagSummary(spec, backfillStreams.length ? backfillStreams : conn.selected_streams);
  const streamCount = backfillStreams.length || (conn.selected_streams || []).length || 0;
  const unpublished = countUnpublishedDays(from, days, lag);
  return { days, streamCount, lag, unpublished };
}

export function emptyRunsMessage(runsCount, hasConnFilter) {
  if (runsCount === 0) {
    return hasConnFilter
      ? "This connection hasn't run yet in Dagster."
      : 'No Dagster runs in this workspace yet.';
  }
  return 'No runs in the selected time window.';
}

export function buildAssetRows(assets, mats) {
  return assets.map((a) => {
    const m = mats.find((mm) => mm.asset_key === a);
    return {
      asset_key: a,
      partition: m?.partition,
      timestamp: m?.timestamp,
      materialized: !!m,
    };
  });
}

export function buildTagRows(tags) {
  return Object.entries(tags || {}).map(([k, v]) => ({ k, v }));
}

/**
 * SyncHistoryDialog — Dagster-backed sync activity, rendered as a modal
 * launched from the Data Sources page. Lists tenant-scoped runs from
 * `GET /syncs/dagit/*` (Dagster GraphQL proxy) with run-detail + backfill
 * drawers layered on top.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   connectionId?: string  // optional pre-filter
 */
function HistoryTitle({ hasActive }) {
  return (
    <Space align="center">
      <HistoryOutlined />
      <span>Sync History</span>
      <Tag color="purple" icon={<ApiOutlined />}>Dagit</Tag>
      {hasActive && (
        <Tag color="processing">
          <SyncOutlined spin /> Live
        </Tag>
      )}
    </Space>
  );
}

// Connection / status / time filters, and the Dagit, Backfill and Refresh buttons.
function HistoryFilters({
  lockedToConn, filterConn, setFilterConn, connections, filterStatus, setFilterStatus,
  filterRange, setFilterRange, orchestratorAbsent, onBackfill, refresh,
}) {
  return (
    <Space wrap style={{ marginBottom: 12 }}>
      <FilterOutlined style={{ color: '#8c8c8c' }} />
      <Tooltip title={lockedToConn ? 'Opened from a source — this filter is locked to that connection.' : ''}>
        <Select
          allowClear={!lockedToConn}
          disabled={lockedToConn}
          placeholder="All connections"
          value={filterConn || undefined}
          onChange={(v) => setFilterConn(v || '')}
          style={{ minWidth: 240 }}
          options={connections.map((c) => ({
            value: c.id,
            label: `${c.name} · ${c.connector_name}`,
          }))}
        />
      </Tooltip>
      <Select
        allowClear
        placeholder="Any status"
        value={filterStatus || undefined}
        onChange={(v) => setFilterStatus(v || '')}
        style={{ minWidth: 160 }}
        options={[
          { value: 'SUCCESS', label: 'Success' },
          { value: 'FAILURE', label: 'Failure' },
          { value: 'STARTED', label: 'Running' },
          { value: 'QUEUED', label: 'Queued' },
          { value: 'CANCELED', label: 'Canceled' },
        ]}
      />
      <Tooltip title="Filter runs by start time (client-side, applied to runs already fetched).">
        <RangePicker
          value={filterRange}
          onChange={(v) => setFilterRange(v)}
          allowEmpty={[true, true]}
          presets={[
            { label: 'Today', value: [dayjs().startOf('day'),         dayjs().endOf('day')] },
            { label: 'Last 24h', value: [dayjs().subtract(24, 'hour'),   dayjs()] },
            { label: 'Last 7d', value: [dayjs().subtract(7,  'day').startOf('day'),  dayjs().endOf('day')] },
            { label: 'Last 30d', value: [dayjs().subtract(30, 'day').startOf('day'),  dayjs().endOf('day')] },
            { label: 'Last 90d', value: [dayjs().subtract(90, 'day').startOf('day'),  dayjs().endOf('day')] },
          ]}
          disabledDate={(d) => d && d > dayjs().endOf('day')}
          style={{ minWidth: 260 }}
        />
      </Tooltip>
      {/* Hidden unless Dagit is BOTH enabled on this deployment and actually
          running — see the orchestratorAbsent fetch above. The per-run
          "Dagit" button in the drawer needs no such check: it only renders
          once a run is open, and runs only exist when the orchestrator
          answered. */}
      {isDagitEnabled() && !orchestratorAbsent && (
        <Tooltip title="Open Dagit UI in a new tab">
          <Button icon={<ExportOutlined />} onClick={() => openDagster('/')}>
            Open Dagit
          </Button>
        </Tooltip>
      )}
      <Tooltip title="Re-run a connection over a date range. Each day becomes one Dagster partition run.">
        <Button
          icon={<HistoryOutlined />}
          onClick={onBackfill}
        >
          Backfill…
        </Button>
      </Tooltip>
      <Button icon={<ReloadOutlined />} onClick={refresh}>Refresh</Button>
    </Space>
  );
}

// The run timeline: a spinner while loading, an empty state, or the runs with
// notes on what the fetch limit and the time filter left out.
function RunListBody({ loading, runs, visibleRuns, filterConn, connBy, openDrawer }) {
  if (loading) {return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;}
  if (visibleRuns.length === 0) {
    return (
      <Empty description={emptyRunsMessage(runs.length, !!filterConn)} />
    );
  }
  return (
      <>
        {runs.length >= RUNS_FETCH_LIMIT && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            title={`Showing the most recent ${RUNS_FETCH_LIMIT} runs. Older runs are not loaded — narrow by connection or status to see history beyond this window.`}
          />
        )}
        {visibleRuns.length < runs.length && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            title={`Time filter applied: showing ${visibleRuns.length} of ${runs.length} fetched runs.`}
          />
        )}
        <Timeline
        mode="start"
        style={{ padding: 15 }}
        items={visibleRuns.map((r) => ({
          icon: statusDot(r.status),
          color: dotColor(r.status),
          title: (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {fmtTime(r.start_time || r.creation_time)}
            </Text>
          ),
          content: (
            <RunRow
              run={r}
              conn={connBy[r.connection_id]}
              onOpen={() => openDrawer(r)}
            />
          ),
        }))}
      />
      </>
  );
}

// One run: its id, status and Dagit link, then the detail once loaded.
function RunDrawer({ openRun, detailLoading, openDetail, connBy, onClose }) {
  return (
    <Drawer
      size={Math.min(820, typeof window !== 'undefined' ? window.innerWidth : 820)}
      open={!!openRun}
      onClose={onClose}
      title={openRun && (
        <Space>
          <code>{String(openRun.id).slice(0, 8)}</code>
          <StatusTag status={openRun.status} />
          {isDagitEnabled() && (
            <Tooltip title="Open this run in Dagit">
              <Button
                size="small"
                icon={<ExportOutlined />}
                onClick={() => openDagsterRun(openRun.id)}
              >
                Dagit
              </Button>
            </Tooltip>
          )}
        </Space>
      )}
    >
      {detailLoading && <Spin />}
      {openDetail && <RunDetail detail={openDetail} conn={connBy[openDetail.connection_id]} />}
    </Drawer>
  );
}

// What a backfill will load, and which of its days the source has not published.
function BackfillPlanAlert({ backfillPlan }) {
  return (
    <Alert
      type={backfillPlan.unpublished ? 'warning' : 'info'}
      showIcon
      icon={<ClockCircleFilled />}
      style={{ marginBottom: 16 }}
      title={
        <Text>
          Will load{' '}
          <Text strong>
            {backfillPlan.days} day{backfillPlan.days === 1 ? '' : 's'}
          </Text>
          {' '}× <Text strong>{backfillPlan.streamCount}</Text> table
          {backfillPlan.streamCount === 1 ? '' : 's'} — each day as
          itself, no trailing window.
        </Text>
      }
      description={
        <Text type="secondary" style={{ fontSize: 12 }}>
          {backfillPlan.lag && (
            <>
              This source publishes with a {backfillPlan.lag.label}{' '}
              delay; the newest day it holds is{' '}
              <Text strong>{backfillPlan.lag.freshestDay}</Text> (UTC).
              {backfillPlan.unpublished > 0 && (
                <>
                  {' '}
                  <Text strong>
                    {backfillPlan.unpublished} day
                    {backfillPlan.unpublished === 1 ? '' : 's'} in this
                    range {backfillPlan.unpublished === 1 ? 'is' : 'are'}{' '}
                    not published yet
                  </Text>
                  {' '}and will load nothing — re-run them once the
                  source catches up.
                </>
              )}
            </>
          )}
        </Text>
      }
    />
  );
}

function BackfillResultAlert({ backfillResult }) {
  return (
    <Alert
      type={backfillResult.skipped ? 'warning' : 'success'}
      showIcon
      style={{ marginBottom: 16 }}
      title={`Launched ${backfillResult.launched} run(s); ${backfillResult.skipped} skipped.`}
      description={backfillResult.error ? <Text code>{backfillResult.error}</Text> : null}
    />
  );
}

// Re-run a connection over a date range, one Dagster partition run per day.
function BackfillDrawer({
  open, onClose, lockedToConn, connections, backfillConn, setBackfillConn, setBackfillStreams,
  backfillRange, setBackfillRange, backfillStreams, backfillStreamOptions, backfillPlan,
  backfillResult, backfillSubmitting, submitBackfill,
}) {
  return (
    <Drawer
      size={Math.min(560, typeof window !== 'undefined' ? window.innerWidth : 560)}
      open={open}
      onClose={onClose}
      title={<Space><HistoryOutlined /> Backfill date range</Space>}
      destroyOnHidden
    >
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Re-run a connection across a date window. Each day is loaded from the
        source as itself — pick 7 days and you get those 7 days, one Dagster
        run and one source request per day. If you specify streams, only
        those assets are materialized.
      </Paragraph>
      <Form layout="vertical">
        <Form.Item
          label="Connection"
          required
          extra={lockedToConn ? 'Locked to the source you opened Sync History from.' : null}
        >
          <Select
            placeholder="Pick a connection"
            value={backfillConn || undefined}
            onChange={(v) => { setBackfillConn(v); setBackfillStreams([]); }}
            disabled={lockedToConn}
            options={connections.map((c) => ({
              value: c.id,
              label: `${c.name} · ${c.connector_name}`,
            }))}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item label="Date range (UTC, inclusive)" required>
          <RangePicker
            value={backfillRange}
            onChange={(v) => setBackfillRange(v)}
            disabledDate={(d) => d && d > dayjs().endOf('day')}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item label="Streams (optional)">
          <Select
            mode="multiple"
            allowClear
            placeholder="All selected streams"
            value={backfillStreams}
            onChange={setBackfillStreams}
            options={backfillStreamOptions}
            disabled={!backfillConn}
          />
        </Form.Item>
        {backfillPlan && <BackfillPlanAlert backfillPlan={backfillPlan} />}
        {backfillResult && <BackfillResultAlert backfillResult={backfillResult} />}
        <Space>
          <Button
            type="primary"
            loading={backfillSubmitting}
            onClick={submitBackfill}
            icon={<HistoryOutlined />}
          >
            Launch backfill
          </Button>
          <Button onClick={onClose} disabled={backfillSubmitting}>
            Close
          </Button>
        </Space>
      </Form>
    </Drawer>
  );
}

export default function SyncHistoryDialog({ open, onClose, connectionId = '' }) {
  // When `connectionId` is supplied by the caller, the dialog is scoped
  // to that source — the connection filter (and backfill connection
  // picker) becomes read-only so users can't accidentally pull up logs
  // for a different source from inside the per-source view.
  const lockedToConn = !!connectionId;
  const [filterConn, setFilterConn] = useState(connectionId || '');
  const [filterStatus, setFilterStatus] = useState('');
  // Time filter — client-side window applied to `runs`. `null` means no
  // bound on that side. Defaults to the last 7 days so the modal opens
  // showing a useful slice of recent activity rather than the full
  // RUNS_FETCH_LIMIT firehose.
  const [filterRange, setFilterRange] = useState(() => {
    const end = dayjs().endOf('day');
    return [end.subtract(DEFAULT_RANGE_DAYS, 'day').startOf('day'), end];
  });

  const [runs, setRuns] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRun, setOpenRun] = useState(null);
  const [openDetail, setOpenDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillSubmitting, setBackfillSubmitting] = useState(false);
  const [backfillConn, setBackfillConn] = useState(connectionId || null);
  const [backfillRange, setBackfillRange] = useState(() => {
    const today = dayjs().startOf('day');
    return [today.subtract(DEFAULT_RANGE_DAYS, 'day'), today];
  });
  const [backfillStreams, setBackfillStreams] = useState([]);
  const [backfillResult, setBackfillResult] = useState(null);
  // Catalog specs, for the per-stream `data_delay_days` the backfill notice
  // reads. Connections carry stream NAMES only, so the source's publication
  // lag has to come from the catalog. Fetched once, lazily, when the backfill
  // drawer is first opened — it is not needed to render the run list.
  const [specs, setSpecs] = useState([]);
  useEffect(() => {
    if (!backfillOpen || specs.length) {return undefined;}
    let cancelled = false;
    listConnectorSpecs()
      .then((s) => { if (!cancelled) {setSpecs(s || []);} })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [backfillOpen, specs.length]);

  // Reset the connection filter to the prop value each time the dialog opens.
  useEffect(() => {
    if (open) {
      setFilterConn(connectionId || '');
      setBackfillConn(connectionId || null);
    }
  }, [open, connectionId]);

  // Is the orchestrator actually up? Needed to decide whether the "Open Dagit"
  // button below is a working link or a 503: the reverse proxy is happy to be
  // mounted while the Dagster webserver is scaled to zero, which is the normal
  // state on a cost-reduced environment. Fetched once per open rather than
  // threaded in as a prop, because this dialog is opened from three different
  // pages and only one of them polls the scheduler.
  //
  // Starts `true` (absent) so the button cannot flash on before the answer
  // arrives; a failed fetch keeps it absent, which is the safe reading.
  const [orchestratorAbsent, setOrchestratorAbsent] = useState(true);
  useEffect(() => {
    if (!open) {return undefined;}
    let cancelled = false;
    getSchedulerStatus()
      .then((s) => {
        if (!cancelled) {setOrchestratorAbsent(s?.orchestrator_available === false);}
      })
      .catch(() => { if (!cancelled) {setOrchestratorAbsent(true);} });
    return () => { cancelled = true; };
  }, [open]);

  const refresh = useCallback(async () => {
    if (!open) {return;}
    try {
      const [r, c] = await Promise.all([
        listDagitRuns({
          connectionId: filterConn || undefined,
          status: filterStatus || undefined,
          limit: RUNS_FETCH_LIMIT,
        }),
        connections.length === 0 ? listConnections().catch(() => []) : Promise.resolve(connections),
      ]);
      setRuns(r || []);
      if (connections.length === 0) {setConnections(c || []);}
    } catch (err) {
      message.error(`Failed to load Dagster runs: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [open, filterConn, filterStatus, connections]);

  useEffect(() => {
    if (open) {
      setLoading(true);
      refresh();
    }
  }, [open, refresh]);

  // Live-refresh while anything is queued/started/running.
  const hasActive = useMemo(() => computeHasActiveRuns(runs), [runs]);
  useEffect(() => {
    if (!open || !hasActive) {return undefined;}
    const t = setInterval(refresh, ACTIVE_REFRESH_MS);
    return () => clearInterval(t);
  }, [open, hasActive, refresh]);

  const connBy = useMemo(() => buildConnById(connections), [connections]);

  // Client-side time-window filter: keep runs whose start_time (falling
  // back to creation_time) lies within `filterRange`. Either bound can
  // be empty — an empty `from` means "since the beginning of time", an
  // empty `to` means "until now".
  const visibleRuns = useMemo(() => filterRunsByTimeWindow(runs, filterRange), [runs, filterRange]);

  const openDrawer = async (run) => {
    setOpenRun(run);
    setOpenDetail(null);
    setDetailLoading(true);
    try {
      const detail = await getDagitRun(run.id);
      setOpenDetail(detail);
    } catch (err) {
      message.error(`Couldn't load run details: ${err.message}`);
    } finally {
      setDetailLoading(false);
    }
  };

  const submitBackfill = async () => {
    if (!backfillConn) {
      message.error('Pick a connection to backfill.');
      return;
    }
    if (!backfillRange || !backfillRange[0] || !backfillRange[1]) {
      message.error('Pick a date range.');
      return;
    }
    setBackfillSubmitting(true);
    setBackfillResult(null);
    try {
      const res = await launchDagitBackfill({
        connectionId: backfillConn,
        fromDate: backfillRange[0].format(ISO_DATE_FMT),
        toDate: backfillRange[1].format(ISO_DATE_FMT),
        streams: backfillStreams,
      });
      setBackfillResult(res);
      if (res.launched > 0) {
        message.success(`Launched ${res.launched} run${res.launched === 1 ? '' : 's'}.`);
        refresh();
      }
      if (res.skipped > 0) {
        message.warning(`${res.skipped} day(s) skipped: ${res.error || 'see Dagit'}`);
      }
    } catch (err) {
      message.error(`Backfill failed: ${err.message}`);
    } finally {
      setBackfillSubmitting(false);
    }
  };

  // `GET /connections` returns `selected_streams` — a list of NAMES. It has
  // no `streams` field (stream objects with cursor_field/PK live on the
  // catalog entry, not the connection), so reading `conn.streams` left this
  // dropdown permanently on "No data" and every backfill silently ran ALL
  // selected streams. For a connector like claude-code that is 7 streams of
  // rate-limited API calls per backfilled day instead of the one the
  // operator wanted.
  const backfillStreamOptions = useMemo(
    () => buildBackfillStreamOptions(connBy[backfillConn]),
    [backfillConn, connBy],
  );

  // What the operator is about to get. Spelled out because the two obvious
  // questions — "how many days will this actually load?" and "why did the
  // last day come back empty?" — have non-obvious answers: a backfill run
  // loads exactly its own day (`exact_window`, see
  // `orchestrator/assets/base.py`), and a day the source has not published
  // yet loads nothing at all.
  const backfillPlan = useMemo(
    () => computeBackfillPlan(connBy[backfillConn], backfillRange?.[0], backfillRange?.[1], backfillStreams, specs),
    [backfillConn, connBy, backfillRange, backfillStreams, specs],
  );

  const startBackfill = () => {
    setBackfillResult(null);
    setBackfillConn(filterConn || null);
    setBackfillStreams([]);
    setBackfillOpen(true);
  };
  const closeRun = () => { setOpenRun(null); setOpenDetail(null); };
  const closeBackfill = () => setBackfillOpen(false);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={Math.min(1100, typeof window !== 'undefined' ? window.innerWidth - 48 : 1100)}
      destroyOnHidden
      title={<HistoryTitle hasActive={hasActive} />}
    >
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Runs sourced live from the Dagster orchestrator. Tenant-scoped via run tags.
      </Paragraph>

      <HistoryFilters
        lockedToConn={lockedToConn} filterConn={filterConn} setFilterConn={setFilterConn}
        connections={connections} filterStatus={filterStatus} setFilterStatus={setFilterStatus}
        filterRange={filterRange} setFilterRange={setFilterRange} orchestratorAbsent={orchestratorAbsent}
        onBackfill={startBackfill} refresh={refresh}
      />

      <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
        <RunListBody
          loading={loading} runs={runs} visibleRuns={visibleRuns} filterConn={filterConn}
          connBy={connBy} openDrawer={openDrawer}
        />
      </div>

      <RunDrawer
        openRun={openRun} detailLoading={detailLoading} openDetail={openDetail}
        connBy={connBy} onClose={closeRun}
      />

      <BackfillDrawer
        open={backfillOpen} onClose={closeBackfill} lockedToConn={lockedToConn} connections={connections}
        backfillConn={backfillConn} setBackfillConn={setBackfillConn} setBackfillStreams={setBackfillStreams}
        backfillRange={backfillRange} setBackfillRange={setBackfillRange} backfillStreams={backfillStreams}
        backfillStreamOptions={backfillStreamOptions} backfillPlan={backfillPlan}
        backfillResult={backfillResult} backfillSubmitting={backfillSubmitting} submitBackfill={submitBackfill}
      />
    </Modal>
  );
}

// ── Timeline row ─────────────────────────────────────────────────
function RunRow({ run, conn, onOpen }) {
  const dur = duration(run.start_time, run.end_time);
  const assets = run.asset_selection || [];
  return (
    <Card
      size="small"
      hoverable
      onClick={onOpen}
      style={{ marginBottom: 4 }}
      styles={{ body: { padding: 12 } }}
    >
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 600 }}>
            {conn?.name || run.connector || run.job_name}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {run.job_name}
            {run.triggered_by && <> · <Tag style={{ margin: 0 }}>{run.triggered_by}</Tag></>}
          </Text>
        </div>
        <Space size={16}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 600 }}>
              <DatabaseOutlined /> {assets.length} asset{assets.length === 1 ? '' : 's'}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>{dur}</Text>
          </div>
          <StatusTag status={run.status} />
        </Space>
      </Space>
    </Card>
  );
}

// ── Drawer body ──────────────────────────────────────────────────
function RunSummary({ detail, conn }) {
  return (
    <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
      <Descriptions.Item label="Connection" span={2}>
        {conn?.name || detail.connection_id || '—'}
      </Descriptions.Item>
      <Descriptions.Item label="Connector">{detail.connector || '—'}</Descriptions.Item>
      <Descriptions.Item label="Job">{detail.job_name}</Descriptions.Item>
      <Descriptions.Item label="Status"><StatusTag status={detail.status} /></Descriptions.Item>
      <Descriptions.Item label="Triggered by">{detail.triggered_by || '—'}</Descriptions.Item>
      <Descriptions.Item label="Started">{fmtTime(detail.start_time) || '—'}</Descriptions.Item>
      <Descriptions.Item label="Finished">{fmtTime(detail.end_time) || '—'}</Descriptions.Item>
      <Descriptions.Item label="Duration">
        {duration(detail.start_time, detail.end_time)}
      </Descriptions.Item>
      <Descriptions.Item label="Steps">
        {detail.steps_succeeded ?? 0} ok / {detail.steps_failed ?? 0} failed
      </Descriptions.Item>
    </Descriptions>
  );
}

function RunAssets({ assets, mats }) {
  return (
    <>
      <Title level={5} style={{ marginBottom: 8 }}>
        Assets ({mats.length}/{assets.length} materialized)
      </Title>
      <Table
        size="small"
        pagination={false}
        rowKey={(r) => r.asset_key + (r.partition || '')}
        dataSource={buildAssetRows(assets, mats)}
        columns={[
          {
            title: 'Asset', dataIndex: 'asset_key',
            render: (v) => <code style={{ fontSize: 12 }}>{v}</code>,
          },
          {
            title: 'Partition', dataIndex: 'partition',
            render: (v) => v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
          },
          {
            title: 'At', dataIndex: 'timestamp',
            render: (v) => v ? fmtTime(v) : '—',
          },
          {
            title: '', dataIndex: 'materialized', width: 80,
            render: (v) => v
              ? <CheckCircleFilled style={{ color: '#52c41a' }} />
              : <ClockCircleFilled style={{ color: '#bfbfbf' }} />,
          },
        ]}
        style={{ marginBottom: 16 }}
      />
    </>
  );
}

function RunTags({ tags }) {
  if (!(tags && Object.keys(tags).length > 0)) {return null;}
  return (
    <Collapse
      size="small"
      style={{ marginBottom: 16 }}
      items={[{
        key: 'tags',
        label: <span>Tags ({Object.keys(tags).length})</span>,
        children: (
          <Table
            size="small"
            showHeader={false}
            pagination={false}
            rowKey={(r) => r.k}
            dataSource={buildTagRows(tags)}
            columns={[
              { dataIndex: 'k', width: 160, render: (v) => <Text type="secondary">{v}</Text> },
              { dataIndex: 'v', render: (v) => <code style={{ fontSize: 12 }}>{v}</code> },
            ]}
          />
        ),
      }]}
    />
  );
}

function RunDetail({ detail, conn }) {
  const mats = detail.materializations || [];
  const assets = detail.asset_selection || [];

  return (
    <>
      <RunSummary detail={detail} conn={conn} />

      {assets.length > 0 && <RunAssets assets={assets} mats={mats} />}

      <RunTags tags={detail.tags} />

      <RunLogs runId={detail.id} />
    </>
  );
}

function RunLogs({ runId }) {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const fetchPage = useCallback(async (after = null, append = false) => {
    setLoading(true);
    try {
      const page = await getDagitRunLogs(runId, after);
      setEvents((prev) => append ? [...prev, ...page.events] : page.events);
      setCursor(page.cursor);
      setHasMore(page.has_more);
    } catch (err) {
      message.error(`Couldn't load logs: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { fetchPage(null, false); }, [fetchPage]);

  const filtered = useMemo(() => {
    if (!filter) {return events;}
    const f = filter.toLowerCase();
    return events.filter((e) =>
      (e.message || '').toLowerCase().includes(f) ||
      (e.step_key || '').toLowerCase().includes(f) ||
      (e.event_type || '').toLowerCase().includes(f),
    );
  }, [events, filter]);

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Title level={5} style={{ margin: 0 }}>Event log</Title>
        <Space>
          <Input.Search
            allowClear
            placeholder="Filter…"
            size="small"
            style={{ width: 200 }}
            onSearch={setFilter}
            onChange={(e) => !e.target.value && setFilter('')}
          />
          <Button size="small" onClick={() => fetchPage(null, false)} loading={loading}>
            Reload
          </Button>
        </Space>
      </Space>
      <div style={{
        background: '#0b1020', color: '#d4dbe7', borderRadius: 4,
        padding: 8, fontFamily: 'monospace', fontSize: 11,
        maxHeight: 360, overflow: 'auto',
      }}>
        {filtered.length === 0 && !loading && (
          <Text type="secondary" style={{ color: '#8c8c8c' }}>No events.</Text>
        )}
        {filtered.map((e, i) => (
          <div key={i} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            <span style={{ color: '#7d8a99' }}>{fmtTime(e.timestamp)}</span>{' '}
            <span style={{ color: levelColor(e.level) }}>{e.level || 'INFO'}</span>{' '}
            {e.step_key && <span style={{ color: '#9bbcff' }}>[{e.step_key}]</span>}{' '}
            {e.message}
          </div>
        ))}
      </div>
      {hasMore && (
        <Button
          size="small"
          style={{ marginTop: 8 }}
          onClick={() => fetchPage(cursor, true)}
          loading={loading}
        >
          Load more
        </Button>
      )}
    </>
  );
}

// ── helpers ──────────────────────────────────────────────────────
function StatusTag({ status }) {
  const map = {
    success:    { color: 'success',    icon: <CheckCircleFilled /> },
    failure:    { color: 'error',      icon: <CloseCircleFilled /> },
    started:    { color: 'processing', icon: <SyncOutlined spin /> },
    starting:   { color: 'processing', icon: <SyncOutlined spin /> },
    queued:     { color: 'default',    icon: <ClockCircleFilled /> },
    not_started:{ color: 'default',    icon: <ClockCircleFilled /> },
    canceled:   { color: 'default',    icon: <CloseCircleFilled /> },
    cancelled:  { color: 'default',    icon: <CloseCircleFilled /> },
  };
  const m = map[(status || '').toLowerCase()] || { color: 'default', icon: <ClockCircleFilled /> };
  return <Tag color={m.color} icon={m.icon} style={{ margin: 0 }}>{status || 'unknown'}</Tag>;
}
function statusDot(status) {
  const map = {
    success:  <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />,
    failure:  <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 16 }} />,
    started:  <SyncOutlined spin style={{ color: '#1677ff', fontSize: 16 }} />,
    starting: <SyncOutlined spin style={{ color: '#1677ff', fontSize: 16 }} />,
    queued:   <ClockCircleFilled style={{ color: '#bfbfbf', fontSize: 16 }} />,
  };
  return map[(status || '').toLowerCase()]
    || <ClockCircleFilled style={{ color: '#bfbfbf', fontSize: 16 }} />;
}
function dotColor(status) {
  return ({
    success: 'green', failure: 'red',
    started: 'blue', starting: 'blue',
    queued: 'gray', canceled: 'gray', cancelled: 'gray',
  })[(status || '').toLowerCase()] || 'gray';
}
function levelColor(lvl) {
  return ({
    DEBUG: '#7d8a99', INFO: '#a8c0ff',
    WARNING: '#ffd666', ERROR: '#ff7875', CRITICAL: '#ff4d4f',
  })[lvl] || '#d4dbe7';
}
function fmtTime(iso) {
  if (!iso) {return '';}
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function duration(start, end) {
  if (!start) {return '—';}
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  if (Number.isNaN(s) || Number.isNaN(e)) {return '—';}
  const ms = Math.max(0, e - s);
  if (ms < 1000) {return `${ms}ms`;}
  if (ms < MS_PER_MINUTE) {return `${(ms / 1000).toFixed(1)}s`;}
  const m = Math.floor(ms / MS_PER_MINUTE);
  const sec = Math.floor((ms % MS_PER_MINUTE) / 1000);
  return `${m}m ${sec}s`;
}
